import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, inject, Output, signal } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { catchError, finalize, forkJoin, of, throwError, map } from 'rxjs';
import { RequisicionesCandidatoService } from 'src/app/apps/nodo/modules/seleccion/requisiciones/services/requisiciones-candidato.service';
import { StackDirectives } from 'src/app/shared/directives/stack-directives';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { MasterService } from 'src/app/shared/services/master.service';
import { MiAdmPersonas } from '../../interfaces/IAdmPersonas';
import { ICandidateInfo } from '../../interfaces/ICandidatoInfo';
import { CommonModule } from '@angular/common';
import { IDropdown } from 'src/app/apps/nodo/modules/administracion/interfaces/IDropdown';
import { Select } from 'primeng/select';
import { PersonService } from '../../services/person.service';
import { PersonConfirmationDto, PersonCorrectionDto } from '../../interfaces/IPersonDataBasicDto';
import { ToastrService } from 'ngx-toastr';
import { SoloNumerosDirective } from 'src/app/shared/directives/solo-numeros.directive';
import { sanitizarDocumento } from 'src/app/shared/utils/documento.util';

type Mode = 'OK' | 'FIX' | null;

@Component({
    selector: 'app-pre-registro',
    imports: [ReactiveFormsModule, InputTextModule, StackDirectives, CommonModule, Select, SoloNumerosDirective],
    templateUrl: './pre-registro.html',
    styleUrl: './pre-registro.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PreRegistro {
    private masterService = inject(MasterService);
    private requisitionCandidateService = inject(RequisicionesCandidatoService);
    private loader = inject(LoaderService);
    private ac = inject(ActivatedRoute);
    private fb = inject(FormBuilder);
    private personService = inject(PersonService);
    private cdr = inject(ChangeDetectorRef);
    private toastr = inject(ToastrService);
    private router = inject(Router);

    @Output() validChange = new EventEmitter<boolean>();
    @Output() requiereVehiculoChange = new EventEmitter<boolean>();
    /** Datos de la requisición para el encabezado de la página (vacante, número de proceso, reingreso). */
    @Output() infoLoaded = new EventEmitter<ICandidateInfo>();
    idEncrypt: any;

    dataCandidate: ICandidateInfo | null;
    identificationList: IDropdown[] = [];

    form!: FormGroup;
    isSubmitting = false;

    showFixOption = signal(false);

    ngOnInit(): void {
        this.form = this.fb.group({
            mode: [null as Mode],
            accept: this.fb.group({
                tipoDoc: [null, [Validators.required]],
                codEmp: ['', [Validators.required, Validators.pattern(/^\d+$/), Validators.minLength(5)]]
            }),
            correction: this.fb.group(
                {
                    tipoDoc1: [null, [Validators.required]],
                    tipoDoc2: [null, [Validators.required]],
                    docNum1: ['', [Validators.required, Validators.pattern(/^\d+$/), Validators.minLength(5)]],
                    docNum2: ['', [Validators.required, Validators.pattern(/^\d+$/), Validators.minLength(5)]]
                },
                {
                    validators: [
                        this.mustMatch('tipoDoc1', 'tipoDoc2', 'tipoDocMustMatch'),
                        this.mustMatch('docNum1', 'docNum2', 'docNumMustMatch')
                    ]
                }
            )
        });

        // Por defecto, deshabilitamos ambos subgrupos hasta que se elija un modo
        this.correctionGroup.disable();
        this.setMode('OK');

        this.ac.queryParams.subscribe((params) => {
            const rawId = params['q'];
            if (rawId) {
                this.idEncrypt = encodeURIComponent(rawId);
                this.loadData();
            } else {
                this.router.navigate(['/login']);
            }
        });
    }

    loadData() {
        this.loader.show('Cargando información...');
        forkJoin({
            person: this.requisitionCandidateService.getInfoCandidateById(this.idEncrypt),
            documents: this.masterService
                .getIdentificationTypes()
                .pipe(catchError((err) => (err.status === 204 ? of({ data: [] }) : throwError(() => err))))
        })
            .pipe(finalize(() => this.loader.hide()))
            .subscribe({
                next: ({ person, documents }) => {
                    const dataPerson = person.data.candidateInfo;
                    const dataRequisition = person.data.requisition;
                    const dataRequisitionCandidate = person.data.requisitionCandidate;
                    // validaciones por si la requisicion esta cerrada o ya lleno el formulario
                    // if (dataRequisitionCandidate.idEstado === 2 || dataRequisitionCandidate.idEtapa != 1) {
                    //   this.router.navigate(['/info-candidatos/resume'], {
                    //     queryParams: { status: '1' }
                    //   });
                    // }
                    if (
                        dataRequisition.idEstado !== 1 ||
                        dataRequisitionCandidate.idEstado == 5 ||
                        (dataRequisitionCandidate.idEstado == 14 &&
                            dataRequisitionCandidate.idContrato != null &&
                            dataRequisitionCandidate.idContrato != '') ||
                        (dataRequisitionCandidate.idEstado == 8 &&
                            dataRequisitionCandidate.idContrato != null &&
                            dataRequisitionCandidate.idContrato != '')
                    ) {
                        this.router.navigate(['/info-candidatos/resume'], {
                            queryParams: { status: '2' }
                        });
                    }

                    this.dataCandidate = person.data;
                    this.identificationList = documents.data;
                    this.requiereVehiculoChange.emit(!!person.data.requiereVehiculo);
                    this.infoLoaded.emit(person.data);
                    if (person.data.candidateInfo.confirmoPreRegistro) {
                        this.validChange.emit(true);
                    }

                    this.cdr.markForCheck();
                }
            });
    }

    get nombreCompleto(): string {
        // Si dataCandidate o candidateInfo no existen, usa strings vacíos
        const pn = this.dataCandidate?.candidateInfo?.primerNombre ?? '';
        const sn = this.dataCandidate?.candidateInfo?.segundoNombre ?? '';
        const pa = this.dataCandidate?.candidateInfo?.primerApellido ?? '';
        const sa = this.dataCandidate?.candidateInfo?.segundoApellido ?? '';

        return (
            [pn, sn, pa, sa]
                .map((v) => v?.toString().trim())
                .filter((v) => !!v && v.length > 0)
                .join(' ') || '—'
        );
    }

    ofuscarIdentificacion(identificacion: string | undefined): string {
        if (!identificacion) {
            return '—';
        }
        if (!identificacion || identificacion.length <= 4) {
            return '****';
        }

        const visibleDigits = 3;
        const maskedSection = '*'.repeat(identificacion.length - visibleDigits);
        const visibleSection = identificacion.slice(-visibleDigits);
        return maskedSection + visibleSection;
    }

    get tipoDocumento(): string {
        const info = this.dataCandidate as ICandidateInfo;
        const tipoDoc = this.identificationList.find((doc) => doc.id === info.candidateInfo.idTipoIdentificacion.toString());
        return tipoDoc ? tipoDoc.label : '';
    }

    get mode(): Mode {
        return this.form.get('mode')?.value ?? null;
    }
    get acceptGroup(): FormGroup {
        return this.form.get('accept') as FormGroup;
    }
    get correctionGroup(): FormGroup {
        return this.form.get('correction') as FormGroup;
    }

    mustMatch(controlName: string, matchingControlName: string, errorKey: string) {
        return (group: AbstractControl): ValidationErrors | null => {
            const fg = group as FormGroup;
            const c1 = fg.get(controlName);
            const c2 = fg.get(matchingControlName);
            if (!c1 || !c2) return null;

            const equal = c1.value instanceof Object ? JSON.stringify(c1.value) === JSON.stringify(c2.value) : c1.value === c2.value;

            // Recupera errores a nivel grupo (si existen otros)
            const groupErrors = { ...(fg.errors ?? {}) };

            if (!equal) {
                groupErrors[errorKey] = true;
                fg.setErrors(groupErrors);
                // Marca sólo el segundo como con error para resaltar el campo de confirmación
                const c2Errors = { ...(c2.errors ?? {}) };
                c2Errors['mustMatch'] = true;
                c2.setErrors(c2Errors);
                return { [errorKey]: true };
            } else {
                // Limpia errorKey del grupo si ya coincide
                if (groupErrors[errorKey]) {
                    delete groupErrors[errorKey];
                }
                fg.setErrors(Object.keys(groupErrors).length ? groupErrors : null);

                // Limpia el mustMatch del segundo control si existía
                if (c2.errors?.['mustMatch']) {
                    const next = { ...c2.errors };
                    delete next['mustMatch'];
                    c2.setErrors(Object.keys(next).length ? next : null);
                }
                return null;
            }
        };
    }

    setMode(newMode: Mode) {
        this.form.get('mode')?.setValue(newMode);
        if (newMode === 'OK') {
            this.acceptGroup.enable({ emitEvent: true });
            this.acceptGroup.reset();
            this.correctionGroup.disable({ emitEvent: true });
            this.correctionGroup.reset();
        } else if (newMode === 'FIX') {
            this.correctionGroup.enable({ emitEvent: true });
            this.correctionGroup.reset();
            this.acceptGroup.disable({ emitEvent: true });
            this.acceptGroup.reset();
        } else {
            this.acceptGroup.disable();
            this.correctionGroup.disable();
        }
        this.form.markAllAsTouched();
    }

    getErrorMessage(controlName: string, errorMessage: string, groupName?: 'accept' | 'correction'): string | null {
        let control: AbstractControl | null = null;

        if (groupName) {
            control = (this.form.get(groupName) as FormGroup)?.get(controlName) ?? null;
        } else {
            // Permite rutas con punto (e.g., 'accept.codEmp') o controles en raíz
            control = this.form.get(controlName);
        }

        if (!control) return null;

        // Si quieres que aparezca al salir del campo o al escribir:
        const interacted = control.touched || control.dirty;

        if (control.hasError('required') && interacted) {
            return errorMessage ?? 'Este campo es requerido';
        }
        if (control.hasError('pattern') && interacted) {
            return 'Solo se permiten números';
        }
        if (control.hasError('minlength') && interacted) {
            const minLength = control.getError('minlength').requiredLength;
            return `Mínimo ${minLength} caracteres`;
        }
        if (control.hasError('email') && interacted) {
            return 'Correo electrónico inválido';
        }
        if ((control as any).hasError?.('menorDe16') && interacted) {
            return 'Debe ser mayor de 16 años';
        }
        return null;
    }

    isActiveGroupValid(): boolean {
        if (this.mode === 'OK') {
            return this.acceptGroup.valid;
        } else if (this.mode === 'FIX') {
            return this.correctionGroup.valid;
        }
        return false;
    }

    onSubmit() {
        const mode = this.form.get('mode')?.value as 'OK' | 'FIX' | null;

        if (!mode) return;

        const activeGroup = mode === 'OK' ? this.acceptGroup : this.correctionGroup;

        if (mode === 'OK') {
            // Ambos lados sanitizados: el valor registrado puede traer basura heredada y no debe
            // hacer fallar una identificación que sí coincide.
            const ingresada = sanitizarDocumento(this.acceptGroup.get('codEmp')?.value);
            const registrada = sanitizarDocumento(this.dataCandidate?.candidateInfo?.identificacion);
            if (ingresada !== registrada) {
                this.toastr.error('El número de identificación ingresado no coincide con el registrado.', 'Error de validación');
                this.showFixOption.set(true);
                return;
            }
        }

        // Validación previa
        if (!activeGroup.valid) {
            // this.markGroupTouched(activeGroup);
            return;
        }

        // Prevención de doble envío
        if (this.isSubmitting) return;
        this.isSubmitting = true;

        this.loader.show('Enviando información...');
        const payload = mode === 'OK' ? this.buildPayloadForOk() : this.buildPayloadForFix();
        const request$ =
            mode === 'OK' ? this.personService.confirmPreRegister(payload) : this.personService.correctionIdentification(payload);

        request$.pipe(finalize(() => (this.isSubmitting = false))).subscribe({
            next: (response) => {
                console.log('Respuesta del servidor:', response);
                this.loader.hide();

                if (!response.success) {
                    this.toastr.error(response.message || 'No fue posible confirmar la información.');
                    if (mode === 'OK' && this.isNoMatchMessage(response.message)) {
                        this.showFixOption.set(true);
                    }
                    return;
                }

                this.toastr.success('Información enviada con éxito.', 'Operación exitosa');
                if (response.data.confirmoPreRegistro) {
                    this.validChange.emit(true);
                }
            },
            error: (err) => {
                this.loader.hide();
                const errorMessage = err?.error?.mensaje || 'No fue posible enviar la información.';
                this.toastr.error(errorMessage);
                if (mode === 'OK' && this.isNoMatchMessage(errorMessage)) {
                    this.showFixOption.set(true);
                }
            }
        });
    }

    private isNoMatchMessage(message: string | undefined | null): boolean {
        if (!message) return false;
        return message.toLowerCase().includes('no coincide');
    }

    buildPayloadForOk(): PersonConfirmationDto {
        const payload: PersonConfirmationDto = {
            idEncrypted: this.idEncrypt,
            idTipoIdentificacion: Number(this.acceptGroup.get('tipoDoc')?.value),
            codEmp: sanitizarDocumento(this.acceptGroup.get('codEmp')?.value)
        };
        return payload;
    }

    private buildPayloadForFix(): PersonCorrectionDto {
        const payload: PersonCorrectionDto = {
            idEncrypted: this.idEncrypt,
            idTipoIdentificacion: Number(this.correctionGroup.get('tipoDoc1')?.value),
            codEmp: sanitizarDocumento(this.correctionGroup.get('docNum1')?.value)
        };

        return payload;
    }

    onBlockEvent(event: Event): void {
        event.preventDefault();
    }
}
